import { useNavigate, useLocation } from "react-router-dom";

import useScrollSpy from "@/lib/useScrollSpy";

/**
 * Sticky in-page section nav with scroll-spy active state.
 *
 * Props:
 *   sections : [{ id, label }]
 *   lang     : "ja" | "en"   (for testid + locale)
 *   tone     : hex string for the active underline / dot (defaults blue)
 */
export default function SectionNav({ sections, lang, tone = "#0E5FB0", pageKey = "page" }) {
  const ids = sections.map((s) => s.id);
  const active = useScrollSpy(ids);
  const navigate = useNavigate();
  const location = useLocation();

  const jump = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const offset = window.matchMedia("(min-width: 768px)").matches ? 136 : 120;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
    if (location.hash !== `#${id}`) {
      navigate(`${location.pathname}#${id}`, { replace: true });
    }
  };

  return (
    <nav
      data-testid={`${pageKey}-section-nav`}
      className="bg-white/95 backdrop-blur-md border-y border-[#E2E8F0] sticky top-16 md:top-[72px] z-30"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 overflow-x-auto">
        <ul className="flex items-center gap-6 md:gap-10 min-w-max py-4">
          {sections.map((s) => {
            const isActive = active === s.id;
            return (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  onClick={jump(s.id)}
                  data-testid={`${pageKey}-jump-${s.id}`}
                  data-active={isActive || undefined}
                  className="relative inline-flex items-center gap-2 text-[11.5px] tracking-[0.16em] font-mono-display uppercase font-bold transition-colors"
                  style={{ color: isActive ? tone : "#475569" }}
                >
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 rounded-full transition-all"
                    style={{
                      backgroundColor: isActive ? tone : "transparent",
                      border: isActive ? "none" : "1px solid #CBD5E1",
                    }}
                  />
                  {s.label}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
